import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import GhostLogo from "./GhostLogo";
import WaitlistCounter from "./WaitlistCounter";

const HeroSection = () => {
  const stats = [
    { value: "$47B+", label: "Memecoin Market" },
    { value: "26.7%", label: "Annual Growth" },
    { value: "24/7", label: "AI Monitoring" },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center pt-20 md:pt-24 pb-10 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#00D4FF] rounded-full mix-blend-multiply filter blur-[100px] md:blur-[128px] opacity-20 animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#8B5CF6] rounded-full mix-blend-multiply filter blur-[100px] md:blur-[128px] opacity-20 animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute top-1/2 right-1/3 w-48 md:w-64 h-48 md:h-64 bg-[#FF3366] rounded-full mix-blend-multiply filter blur-[80px] md:blur-[100px] opacity-10 animate-pulse" style={{ animationDelay: "2s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-20">
        {/* Mobile: Logo first, then content. Desktop: Side by side */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Mobile Logo - Shows first on mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="lg:hidden relative flex items-center justify-center w-full"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#00D4FF]/20 to-[#8B5CF6]/20 rounded-full blur-3xl" />
            <GhostLogo size={250} className="relative z-10" />
          </motion.div>

          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full glass mb-4 md:mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#00D4FF] animate-pulse" />
              <span className="text-xs md:text-sm font-medium text-[#A1A1AA] font-mono">
                FUTURE GEN AI PRESENTS
              </span>
            </motion.div>

            {/* Main heading */}
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter mb-3 md:mb-4">
              <span className="gradient-text">GHOST</span>
            </h1>
            <h2 className="font-['Unbounded'] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mb-4 md:mb-6">
              Trade Smarter.
              <br />
              <span className="text-[#A1A1AA]">Not Harder.</span>
            </h2>

            {/* Subtitle */}
            <p className="text-base md:text-lg lg:text-xl text-[#A1A1AA] max-w-xl mx-auto lg:mx-0 mb-6 md:mb-8 leading-relaxed px-2 lg:px-0">
              The AI-powered trading simulator that lets you research, test, and
              master meme coin strategies — <span className="text-white">risk-free</span>. 
              Built for everyone. Not just Wall Street.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-8 px-2 lg:px-0">
              <motion.button
                onClick={() => {
                  const element = document.getElementById("cta");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                  }
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-4 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] text-white font-bold text-base md:text-lg hover:shadow-[0_0_40px_rgba(0,212,255,0.5)] transition-all duration-300 ice-hover touch-manipulation active:scale-95"
                data-testid="hero-cta-primary"
              >
                Join the Waitlist — It's Free
                <ArrowRight size={20} />
              </motion.button>
              <motion.a
                href="#how-it-works"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-4 rounded-full glass text-white font-semibold text-base md:text-lg ice-hover touch-manipulation active:scale-95"
                data-testid="hero-cta-secondary"
              >
                <Play size={20} className="text-[#00D4FF]" />
                See How It Works
              </motion.a>
            </div>

            {/* Waitlist Counter */}
            <div className="flex justify-center lg:justify-start px-2 lg:px-0">
              <WaitlistCounter />
            </div>
          </motion.div>

          {/* Desktop Logo - Shows on right side for large screens */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex relative items-center justify-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#00D4FF]/20 to-[#8B5CF6]/20 rounded-full blur-3xl" />
            <GhostLogo size={450} className="relative z-10" />
          </motion.div>
        </div>

        {/* Stats - Responsive grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mt-12 md:mt-16 max-w-3xl mx-auto"
          data-testid="hero-stats"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="text-center p-4 md:p-6 rounded-2xl glass ice-hover"
            >
              <div className="font-['Unbounded'] text-xl sm:text-2xl md:text-3xl font-bold gradient-text mb-1 md:mb-2">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-[#A1A1AA] font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
